import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Accordion, AccordionDetails, AccordionSummary, Alert, Divider, ExpandMoreIcon } from '@breathelife/mui';
import { AutoCompleteMultiple, AutocompleteOption, Box } from '@breathelife/ui-components';
import {
  ApplicationMode,
  ApplicationModes,
  InsuranceModule,
  PdfDocumentType,
  PdfDocumentTypes,
  PlatformType,
  PlatformTypes,
  QuestionnaireBlueprintCopyableOption,
  QuestionnaireBlueprintCopyableOptions,
  QuestionnaireBlueprintRenderOn,
  QuestionnaireBlueprintRenderOns,
} from '@zinnia/form-engine-sdk';

import { getPdfDocumentTypesNotInParent } from '../../../../../Helpers/questionnaireEditor/pdfDocumentTypeInheritance';
import { useCarrierContext } from '../../../../../Hooks';
import styled from '../../../../../Styles/themed-styled-components';
import { BlueprintContexts } from '../Summaries/BlueprintContexts';
import { EditorDropdown } from './EditorDropdown';
import { ModuleSelection } from './ModuleSelection';

type Props = {
  tag: 'section' | 'subsection' | 'question' | 'field';
  disabled: boolean;
  platform: { selected: PlatformType[]; onChange: (values: PlatformType[]) => void };
  renderOn: {
    selected: QuestionnaireBlueprintRenderOn[];
    onChange: (values: QuestionnaireBlueprintRenderOn[]) => void;
  };
  dataLabel: { selected?: string; options: AutocompleteOption[]; onChange: (updatedSelection: string | null) => void };
  pdfDocument?: {
    selected: PdfDocumentType[];
    onChange: (values: PdfDocumentType[]) => void;
    // The effective (possibly inherited) PDF types of this node's parent. Omit at the section level,
    // which has no parent to compare against.
    parentTypes?: PdfDocumentType[];
  };
  insuranceModule?: { selected: InsuranceModule; onChange: (module: InsuranceModule) => void };
  applicationModes?: {
    selected: ApplicationMode[];
    onChange: (updatedType: ApplicationMode[]) => void;
  };
  copyable: {
    selected: QuestionnaireBlueprintCopyableOption;
    onChange: (updatedSelection: string | null) => void;
    disabled?: boolean;
  };
};

const BlueprintContextContainer = styled.div`
  display: flex;
  align-items: center;

  & > :last-child {
    margin-left: 4px;
  }
`;

export function AdvancedBlueprintOptions({
  disabled,
  tag,
  platform,
  renderOn,
  pdfDocument,
  dataLabel,
  insuranceModule,
  applicationModes,
  copyable,
}: Props): ReactElement {
  const { t } = useTranslation();
  const { features } = useCarrierContext();

  const pdfDocumentTypesNotInParent = useMemo(() => {
    if (!pdfDocument?.parentTypes) {
      return [];
    }
    return getPdfDocumentTypesNotInParent(pdfDocument.selected, pdfDocument.parentTypes);
  }, [pdfDocument]);

  const dataLabelEntry = useMemo(() => {
    if (!dataLabel?.options.length) {
      return null;
    }

    const { options, selected, onChange } = dataLabel;
    return (
      <Box pt={2}>
        <EditorDropdown
          label={t('admin.questionnaireManagement.input.dataLabel')}
          defaultOption={options[0]}
          disabled={disabled}
          selectedOptionValue={selected}
          options={options}
          onChange={onChange}
        />
      </Box>
    );
  }, [dataLabel, disabled, t]);

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <BlueprintContextContainer>
          {t(`admin.questionnaireManagement.advancedOptions.${tag}`)}

          <BlueprintContexts
            platforms={platform.selected}
            renderOn={renderOn.selected}
            insuranceModules={insuranceModule && insuranceModule.selected ? [insuranceModule.selected] : undefined}
          />
        </BlueprintContextContainer>
      </AccordionSummary>
      <Divider />
      <AccordionDetails>
        <Box display='flex' flexDirection='column' width='100%'>
          <AutoCompleteMultiple
            id='platform-type-selection'
            label={t('admin.questionnaireManagement.platforms.title')}
            disabled={disabled}
            optionInfo={{
              items: Object.values(PlatformTypes),
              toOption: (item) => ({ value: item, label: t(`admin.questionnaireManagement.platforms.${item}`) }),
              selectedItems: platform.selected,
              onChange: platform.onChange,
            }}
          />
          <Box pt={2}>
            <AutoCompleteMultiple
              id='render-on-selection'
              label={t('admin.questionnaireManagement.rendering.title')}
              disabled={disabled}
              optionInfo={{
                items: Object.values(QuestionnaireBlueprintRenderOns),
                toOption: (item) => ({ value: item, label: t(`admin.questionnaireManagement.rendering.${item}`) }),
                selectedItems: renderOn.selected,
                onChange: renderOn.onChange,
              }}
            />
          </Box>
          {applicationModes && (
            <Box pt={2}>
              <AutoCompleteMultiple
                id='questionnaire-application-mode-selection'
                label={t('admin.questionnaireManagement.applicationMode.title')}
                disabled={disabled}
                optionInfo={{
                  selectedItems: applicationModes.selected,
                  items: Object.values(ApplicationModes),
                  toOption: (text: ApplicationMode) => ({
                    label: t(`admin.questionnaireManagement.applicationMode.${text}`),
                    value: text,
                  }),
                  onChange: applicationModes.onChange,
                }}
              />
            </Box>
          )}
          {pdfDocument && (
            <Box pt={2}>
              <AutoCompleteMultiple
                id='pdf-documents-selection'
                label={
                  tag === 'section'
                    ? t('admin.questionnaireManagement.pdfDocuments.title')
                    : t('admin.questionnaireManagement.pdfDocuments.titleWithInheritHint')
                }
                disabled={disabled}
                optionInfo={{
                  items: Object.values(PdfDocumentTypes),
                  toOption: (item) => ({ value: item, label: t(`admin.questionnaireManagement.pdfDocuments.${item}`) }),
                  selectedItems: pdfDocument.selected,
                  onChange: pdfDocument.onChange,
                }}
              />
              {pdfDocumentTypesNotInParent.length > 0 && (
                <Box pt={1}>
                  <Alert severity='warning'>
                    {t('admin.questionnaireManagement.pdfDocuments.notInParentWarning', {
                      types: pdfDocumentTypesNotInParent
                        .map((type) => t(`admin.questionnaireManagement.pdfDocuments.${type}`))
                        .join(', '),
                    })}
                  </Alert>
                </Box>
              )}
            </Box>
          )}
          {insuranceModule && (
            <Box pt={2}>
              <ModuleSelection
                selectedModule={insuranceModule.selected}
                onChange={insuranceModule.onChange}
                disabled={disabled}
              />
            </Box>
          )}
          {dataLabelEntry}
          {features.applicationCopy?.enabled && (
            <Box pt={2}>
              <EditorDropdown
                label={t('admin.questionnaireManagement.copyable.title')}
                defaultOption={{
                  label: t(`admin.questionnaireManagement.copyable.${QuestionnaireBlueprintCopyableOptions.none}`),
                  value: QuestionnaireBlueprintCopyableOptions.none,
                }}
                disabled={disabled || copyable.disabled}
                selectedOptionValue={copyable.selected}
                options={Object.values(QuestionnaireBlueprintCopyableOptions).map((option) => ({
                  label: t(`admin.questionnaireManagement.copyable.${option}`),
                  value: option,
                }))}
                onChange={copyable.onChange}
              />
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
